import { CTA_ACTION_TYPE, CTA_TYPE, URL_CONFIGURATION } from '@/constants/bot';
import * as Yup from 'yup';

const optionSchema = Yup.object().shape({
  isRequired: Yup.boolean().nullable(),
  type: Yup.string()
    .when('isRequired', {
      is: true,
      then: (schema) =>
        schema.oneOf(Object.values(CTA_TYPE), 'Invalid CTA type').required('CTA type is required'),
      otherwise: (schema) => schema,
    })
    .nullable(),
  label: Yup.string()
    .when(['isRequired'], {
      is: true,
      then: (schema) => schema.required('Label is required'),
      otherwise: (schema) => schema,
    })
    .nullable(),
  config: Yup.object()
    .when(['type', 'isRequired'], ([type, isRequired]) => {
      if (!isRequired) return Yup.object().nullable();

      if (type === CTA_TYPE.LEAD_MAGNET) {
        return Yup.object().shape({
          url: Yup.string().required('URL is required').nullable(),
        });
      }

      return Yup.object().shape({
        action_type: Yup.string()
          .oneOf(Object.values(CTA_ACTION_TYPE), 'Invalid action type')
          .required('Action type is required'),
      });
    })
    .nullable(),
});

export const pageCtaSchema = Yup.object().shape({
  // Either field may be blank for headline-only or greeting-only cards. Clearing
  // both is reserved for the exact-link action in Welcome & Prompts (see the
  // object-level test below) because CTA Settings writes by workspace + URL.
  popup_greeting: Yup.string().nullable(),
  popup_headline: Yup.string().nullable(),
  // Mirrors of what the selected page has saved, filled at hydration and never edited.
  // Only the blank-clear guard below reads them.
  stored_popup_headline: Yup.string().nullable(),
  stored_popup_greeting: Yup.string().nullable(),
  urls: Yup.array().of(Yup.string()).min(1, 'URL is required').nullable(),
  url: Yup.string().nullable(),
  url_configuration: Yup.string()
    .oneOf(Object.values(URL_CONFIGURATION), 'Invalid URL configuration')
    .required('URL configuration is required'),
  option_a: optionSchema,
  option_b: optionSchema,
  option_c: optionSchema,
}).test(
  'no-blank-page-popup-copy',
  'Remove popup copy from Welcome & Prompts.',
  function preventBlankPagePopupCopy(value) {
    const urls = value?.urls || [];
    const headline = value?.popup_headline?.trim() || '';
    const greeting = value?.popup_greeting?.trim() || '';
    // What the selected page actually has saved, captured at hydration. Blank fields on
    // a page that never had popup copy are not a removal — they are simply a page whose
    // copy comes from the site-wide (or AI) fallback, and blocking that save made it
    // impossible to edit or clear such a page's CTA buttons at all, which is how the
    // "cannot remove the buttons" report reached us (Pharos AI /pricing, 2026-08-25).
    const hadStoredCopy = Boolean(
      value?.stored_popup_headline?.trim() || value?.stored_popup_greeting?.trim(),
    );

    // The CTA Settings API updates every matching URL in the workspace, even when
    // only one URL is selected. Keep the deliberate blank-clear path on the
    // link-ID-scoped action in Welcome & Prompts so another bot cannot be changed.
    if (urls.length > 0 && hadStoredCopy && !headline && !greeting) {
      return this.createError({
        path: 'popup_greeting',
        message: 'To remove popup copy, use Remove popup copy override in Welcome & Prompts.',
      });
    }

    return true;
  },
);
